import type { LDContextCommon } from 'launchdarkly-react-client-sdk';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { camelCase, merge } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import type { FeatureFlagSet, LDContextKinds } from 'utils/featureFlagUtils';

import { getFeatureFlagsOverride } from 'utils/featureFlagUtils';
import { isStorybook } from 'utils/storybookUtils';
export type { FeatureFlagSet, LDFeatureFlagsKey } from 'utils/featureFlagUtils';

export interface ContextKindsArgs {
  context: LDContextCommon;
  kind: LDContextKinds;
}

export const useFeatureFlags = () => {
  const featureFlagsOverride = getFeatureFlagsOverride();
  const client = useLDClient();
  const flags = useFlags<FeatureFlagSet>();

  const [hasFlags, setHasFlags] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    void client?.waitUntilReady().then(() => {
      setReady(true);
      setHasFlags(Object.keys(flags).length > 0);
    });
  }, [client, flags]);

  const updateFeatureFlagContext = useCallback(
    async (contexts: ContextKindsArgs[]): Promise<FeatureFlagSet | undefined> => {
      await client?.waitUntilReady();
      const currentLDContext = client?.getContext();

      const updatedFlags = await client?.identify({
        ...currentLDContext,
        ...Object.fromEntries(contexts.map(({ context, kind }) => [kind, context])),
      });

      if (!updatedFlags) {
        return undefined;
      }

      const camelizedFlags = Object.entries(updatedFlags).reduce(
        (accumulator, [key, value]) => ({ ...accumulator, [camelCase(key)]: value }),
        {} as FeatureFlagSet
      );

      return camelizedFlags;
    },
    [client]
  );

  return {
    flags: isStorybook() ? merge({}, flags, featureFlagsOverride) : flags,
    hasFlags,
    ready,
    updateFeatureFlagContext,
  };
};
